import React, { useState } from 'react';
import { useLocale } from 'locale';
import { Table, TableColumn } from 'ds/components/table';
import { InsightType } from 'utils/entities';
import { BuildingPermitTableProps, genericColConfig, BuildingPermitTableColumn, EMPTY_COL_CHAR } from './utils';
import { getBuildingPermitRequestedChangeColumnConfig, BuildingPermitRequestedChangeColumn } from './columns/BuildingPermitRequestedChangeColumn';
import { BuildingPermitLastEventDateColumn } from './columns/BuildingPermitLastEventDateColumn';
import { BuildingPermitItem } from '../types';
import { State } from 'store/state';
import { connect } from 'react-redux';
import { ShowAt } from 'consts/breakpoints';
import { setBuildingPermitIndex } from 'store/state/insightsContext/actions';
import { ExpandedInfoIL } from './expanded-info-il';
import { BuildingPermitColumn } from './columns/styled';
import { activeBuildingPermitIdxSelector } from 'store/state/insightsContext/selectors';
import { SmallText } from 'ds/components/typography';


const BuildingPermitTable: React.FC<BuildingPermitTableProps> = ({
  visibleRowsDesktop,
  visibleRowsMobile,
  paginationType,
  isMobile,
  data,
  setActiveRecordIndex,
  activeRecordIndex,
}) => {

  const { t, formatDate } = useLocale();
  const [ hoveredRowIdx, setHoveredRowIdx ] = useState(null);

  let columns: Array<TableColumn<BuildingPermitItem>> = [];

  const requestDate = {
    label: t('widget.buildingPermit.tableColumnTitle', { title: BuildingPermitTableColumn.RequestDate }),
    renderFn: (item: BuildingPermitItem) => (
      <BuildingPermitColumn col={BuildingPermitTableColumn.RequestDate}>
        <SmallText>
          {formatDate(item.requestDate) || EMPTY_COL_CHAR}
        </SmallText>
      </BuildingPermitColumn>
    ),
    ...genericColConfig('90px'),
  } as TableColumn<BuildingPermitItem>;

  const status = {
    label: t('widget.buildingPermit.tableColumnTitle', { title: BuildingPermitTableColumn.Status }),
    renderFn: (item: BuildingPermitItem) => (
      <BuildingPermitColumn col={BuildingPermitTableColumn.Status}>
        <SmallText>
          {item.status || EMPTY_COL_CHAR}
        </SmallText>
      </BuildingPermitColumn>
    ),
    ...genericColConfig('120px'),
    sortFn: null,
  } as TableColumn<BuildingPermitItem>;

  const requestedChange = {
    label: t('widget.buildingPermit.tableColumnTitle', { title: BuildingPermitTableColumn.RequestedChange }),
    renderFn: (item: BuildingPermitItem) => <BuildingPermitRequestedChangeColumn item={item as BuildingPermitItem}/>,
    ...getBuildingPermitRequestedChangeColumnConfig(),
  } as TableColumn<BuildingPermitItem>;

  const lastEventDate = {
    label: t('widget.buildingPermit.tableColumnTitle', { title: BuildingPermitTableColumn.LastEventDate }),
    renderFn: (item: BuildingPermitItem) => <BuildingPermitLastEventDateColumn item={item as BuildingPermitItem}/>,
    sortFn: (items: BuildingPermitItem[]) => items.sort((a, b) => +new Date(b.lastEventDate) - +new Date(a.lastEventDate)),
    ...genericColConfig('70px'),
  } as TableColumn<BuildingPermitItem>;

  if (isMobile) columns = [ lastEventDate, requestedChange ];
  else {
    columns = [ lastEventDate, requestedChange, status, requestDate ];
  }

  return (
    <ShowAt to={2}>
      {(isMapHiddenRange) => (
        <Table<BuildingPermitItem>
          visibleRows={isMobile ? visibleRowsMobile : visibleRowsDesktop}
          insightType={InsightType.Planning}
          initialSortColumn={0}
          data={data}
          wrapColumnText
          onRowMouseEnter={isMapHiddenRange
            ? undefined
            : (record, applicationNumber) => (setActiveRecordIndex([ record.applicationNumber ]), setHoveredRowIdx(applicationNumber))}
          onRowMouseLeave={() => (setActiveRecordIndex([]), setHoveredRowIdx(null))}
          activeRowFilter={(record) => hoveredRowIdx !== null ? false : activeRecordIndex.includes(record.applicationNumber)}
          isExpandable
          columns={columns}
          expandCol={ExpandedInfoIL}
          paginationType={paginationType}
          alwaysShowExpandIcon
        />
      )}
    </ShowAt>
  );
};

const mapStateToProps = (state: State) => ({
  activeRecordIndex: activeBuildingPermitIdxSelector(state),
});

const mapStateToDispatch = {
  setActiveRecordIndex: setBuildingPermitIndex,
};

export default connect(mapStateToProps, mapStateToDispatch)(BuildingPermitTable);
